import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { parseLeadRequestIdFromSuffix, welcomeCheckLead } from "../services/leadService";
import OnboardingGate from "../components/onboarding/OnboardingGate";
import Logo from "../components/Logo";

/**
 * Prefijos SPA → el resto del pathname (sin query) se reenvía a GET /lead/welcome-check/{resto}
 * en el mismo orden de segmentos. Canónico: `/rules/…`.
 */
const PREFIXES_WITH_SLASH = [
  "/rules/",
  "/v1/lead/welcome-check/",
  "/lead/welcome-check/",
];

const PREFIXES_BARE = ["/rules", "/v1/lead/welcome-check", "/lead/welcome-check"];

function useWelcomePathSuffix() {
  const { pathname } = useLocation();
  for (const prefix of PREFIXES_WITH_SLASH) {
    if (pathname.startsWith(prefix)) {
      return pathname.slice(prefix.length) || "";
    }
  }
  if (PREFIXES_BARE.some((p) => pathname === p || pathname === `${p}/`)) {
    return "";
  }
  return "";
}

function Shell({ children, ...props }) {
  return (
    <div
      className="flex min-h-[100dvh] flex-col items-center justify-center bg-[#0a0a0a] px-6 py-12 text-center text-[#f5f5f5]"
      {...props}
    >
      <div className="w-full max-w-md space-y-8">{children}</div>
    </div>
  );
}

export default function LeadWelcomePage() {
  const suffix = useWelcomePathSuffix();
  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(searchParams.entries());
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ["lead", "welcome-check", suffix, queryParams],
    queryFn: () => welcomeCheckLead(suffix, queryParams),
    enabled: Boolean(suffix),
    retry: false,
  });

  if (!suffix) {
    return (
      <Shell>
        <div className="flex justify-center">
          <Logo size={40} />
        </div>
        <div className="space-y-3">
          <p className="zx-label">Enlace</p>
          <p className="text-sm text-[rgba(255,255,255,0.75)]">
            Este enlace es incompleto o no es válido.
          </p>
        </div>
        <Link
          to="/auth"
          className="inline-block text-xs uppercase tracking-[0.2em] text-[#e9b5c5] hover:text-[#f5f5f5]"
        >
          Ir al acceso
        </Link>
      </Shell>
    );
  }

  if (q.isLoading) {
    return (
      <Shell>
        <div className="flex justify-center">
          <Logo size={40} />
        </div>
        <p className="text-xs uppercase tracking-[0.22em] text-[rgba(255,255,255,0.45)]">
          Validando acceso…
        </p>
      </Shell>
    );
  }

  if (q.isError) {
    return (
      <Shell data-testid="lead-welcome-error">
        <div className="flex justify-center">
          <Logo size={40} />
        </div>
        <p className="text-sm text-[rgba(255,255,255,0.75)]">{q.error?.message}</p>
        <Link
          to="/auth"
          className="inline-block text-xs uppercase tracking-[0.2em] text-[#e9b5c5] hover:text-[#f5f5f5]"
        >
          Ir al acceso
        </Link>
      </Shell>
    );
  }

  if (q.data?.valid !== true) {
    return (
      <Shell data-testid="lead-welcome-invalid">
        <div className="flex justify-center">
          <Logo size={40} />
        </div>
        <div className="space-y-3">
          <p className="zx-label">Acceso</p>
          <p className="text-sm text-[rgba(255,255,255,0.75)]">
            No encontramos a ese miembro o el enlace ya no es válido.
          </p>
        </div>
        <Link
          to="/auth"
          className="inline-block text-xs uppercase tracking-[0.2em] text-[rgba(255,255,255,0.55)] hover:text-[#f5f5f5]"
        >
          Ir al acceso
        </Link>
      </Shell>
    );
  }

  const leadRequestId = parseLeadRequestIdFromSuffix(suffix);

  return (
    <OnboardingGate
      leadRequestId={leadRequestId}
      onComplete={(data) => {
        navigate("/auth", {
          replace: true,
          state: data?.nickname ? { welcomeNick: data.nickname } : undefined,
        });
      }}
    />
  );
}
